import React from "react";
import clsx from "clsx";
import { X } from "lucide-react";

export function Button({ children, variant = "primary", size = "md", className, loading, ...props }) {
  const variants = {
    primary: "bg-primary text-white hover:bg-primary-hover shadow-sm",
    secondary: "bg-stone-100 text-ink hover:bg-stone-200",
    outline: "border border-line bg-white text-ink hover:bg-stone-50",
    danger: "bg-danger text-white hover:bg-red-600",
    success: "bg-success text-white hover:bg-green-600",
    ghost: "text-ink-soft hover:bg-stone-100",
  };
  const sizes = { sm: "px-3 py-1.5 text-xs", md: "px-4 py-2.5 text-sm", lg: "px-5 py-3 text-base" };
  return (
    <button
      className={clsx(
        "inline-flex items-center justify-center gap-2 rounded-xl font-semibold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.98]",
        variants[variant], sizes[size], className
      )}
      disabled={loading || props.disabled}
      {...props}
    >
      {loading && <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />}
      {children}
    </button>
  );
}

export function Card({ children, className, ...props }) {
  return (
    <div className={clsx("cf-card", className)} {...props}>
      {children}
    </div>
  );
}

const TONES = {
  success: "bg-success-light text-success-text",
  warn: "bg-warn-light text-warn-text",
  danger: "bg-danger-light text-danger-text",
  info: "bg-blue-100 text-blue-700",
  mute: "bg-stone-100 text-stone-600",
};

export function Badge({ tone = "mute", children, className, ...rest }) {
  return (
    <span {...rest} className={clsx("inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold", TONES[tone], className)}>
      {children}
    </span>
  );
}

export function Input({ label, className, error, ...props }) {
  return (
    <div className={className}>
      {label && <label className="cf-label">{label}</label>}
      <input className="cf-input" {...props} />
      {error && <p className="mt-1 text-xs text-danger">{error}</p>}
    </div>
  );
}

export function Select({ label, children, className, ...props }) {
  return (
    <div className={className}>
      {label && <label className="cf-label">{label}</label>}
      <select className="cf-input" {...props}>
        {children}
      </select>
    </div>
  );
}

export function Modal({ open, onClose, title, children, footer, size = "md" }) {
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  const sizes = { sm: "max-w-md", md: "max-w-lg", lg: "max-w-2xl", xl: "max-w-4xl" };
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-ink/40 p-0 backdrop-blur-sm sm:items-center sm:p-4" onClick={onClose}>
      <div
        className={clsx("flex max-h-[92vh] w-full animate-fade-up flex-col overflow-hidden rounded-t-3xl bg-white shadow-cardhover sm:max-h-[88vh] sm:rounded-3xl", sizes[size])}
        onClick={(e) => e.stopPropagation()}
        data-testid="modal"
      >
        <div className="flex shrink-0 items-center justify-between border-b border-line px-6 py-4">
          <h3 className="font-heading text-lg font-bold">{title}</h3>
          <button onClick={onClose} className="rounded-lg p-1.5 text-ink-mute transition-colors hover:bg-stone-100" data-testid="modal-close">
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>
        {footer && <div className="flex shrink-0 justify-end gap-2 border-t border-line bg-white px-6 py-4">{footer}</div>}
      </div>
    </div>
  );
}

export function Spinner({ className }) {
  return <span className={clsx("inline-block h-5 w-5 animate-spin rounded-full border-2 border-primary/30 border-t-primary", className)} />;
}

export function PageLoader() {
  return (
    <div className="flex h-64 items-center justify-center">
      <Spinner className="h-8 w-8" />
    </div>
  );
}

export function EmptyState({ image, title, subtitle, action }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-line bg-white/50 px-6 py-12 text-center">
      {image && <img src={image} alt="" className="mb-5 h-40 w-40 object-contain" />}
      <h3 className="font-heading text-lg font-bold text-ink">{title}</h3>
      {subtitle && <p className="mt-1 max-w-sm text-sm text-ink-soft">{subtitle}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function PageHeader({ title, subtitle, children, icon: Icon }) {
  return (
    <div className="sticky top-14 z-10 -mx-4 mb-6 flex flex-col gap-3 border-b border-line/70 bg-app/90 px-4 py-4 backdrop-blur-md sm:flex-row sm:items-center sm:justify-between lg:top-16 lg:-mx-8 lg:px-8">
      <div className="flex items-start gap-3">
        {Icon && (
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-primary-light text-primary">
            <Icon size={22} />
          </div>
        )}
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-ink md:text-3xl">{title}</h1>
          {subtitle && <p className="mt-0.5 text-sm text-ink-soft">{subtitle}</p>}
        </div>
      </div>
      {children && <div className="flex flex-wrap items-center gap-2">{children}</div>}
    </div>
  );
}
